
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { api } from '../../../lib/api';

// Zeigt, was der Server für Uploads nutzt (T-0223). Bearbeitet wird die
// Konfiguration in der .env des Servers — eine zweite Quelle hier hat nur der
// Raum-Upload gelesen, alle anderen Uploads nicht.
type StorageSettingsDto = {
  bucket?: string;
  privateBucket?: string;
  region?: string;
  publicBaseUrl?: string;
  endpointUrl?: string;
  zugang?: 'schluessel' | 'server_rolle' | 'unvollstaendig';
};
type StorageTestResponse = {
  message?: string;
};

const ZUGANG_TEXT: Record<NonNullable<StorageSettingsDto['zugang']>, string> = {
  schluessel: 'Zugangsschlüssel aus der Serverkonfiguration',
  server_rolle: 'Rolle des Servers (kein Zugangsschlüssel gesetzt)',
  unvollstaendig: 'Zugangsschlüssel nur halb gesetzt – Uploads schlagen fehl',
};

const Zeile = ({ label, wert }: { label: string; wert?: string }) => (
  <div>
    <dt className="text-sm text-muted-foreground">{label}</dt>
    <dd className="break-all text-sm font-medium">{wert || <span className="text-muted-foreground">nicht gesetzt</span>}</dd>
  </div>
);

const StorageSettings = () => {
  const [cfg, setCfg] = useState<StorageSettingsDto | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCfg(await api.get<StorageSettingsDto>('/api/settings/storage/s3'));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Speicher-Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const testUpload = async () => {
    setLoading(true);
    try {
      const res = await api.post<StorageTestResponse>('/api/settings/storage/s3/test', {});
      toast.success(res?.message || 'Test erfolgreich');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Test fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Storage · S3</h1>
        <p className="text-muted-foreground">Speicher, den der Server für Bilder, Dokumente und Videos nutzt</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>S3 Konfiguration</CardTitle>
          <CardDescription>
            Die Werte stehen in der Serverkonfiguration (<code>.env.appserver</code>) und gelten für alle Uploads.
            Ohne Zugangsschlüssel nutzt der Server seine eigene Rolle.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Zeile label="Öffentlicher Bucket" wert={cfg?.bucket} />
            <Zeile label="Privater Bucket" wert={cfg?.privateBucket} />
            <Zeile label="Region" wert={cfg?.region} />
            <Zeile label="Öffentliche Basis-URL" wert={cfg?.publicBaseUrl} />
            {cfg?.endpointUrl ? <Zeile label="Endpunkt" wert={cfg.endpointUrl} /> : null}
            <Zeile label="Anmeldung" wert={cfg?.zugang ? ZUGANG_TEXT[cfg.zugang] : undefined} />
          </dl>
          <div className="mt-4 flex gap-2">
            <Button variant="secondary" onClick={testUpload} disabled={loading}>Test-Upload</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Der Test schreibt eine kleine Datei in den öffentlichen Bucket, liest sie zurück und löscht sie wieder.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default StorageSettings;
